/**
 * New Run Form Component for Email Ad Validation
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, TextField, MenuItem, Typography, Card, CardContent, Box } from '@mui/material';
import DatePickerMui from '../../../components/DatePickerMui';
import Button from '../../../components/Button';
import { emailAdValidationStyles } from '../styles';

const emailTypes = [
  { value: 'PROMOTION', label: 'Promotion' },
  { value: 'REMINDER', label: 'Reminder' },
];

const style = emailAdValidationStyles;

const NewRunForm = ({ onSubmit, onCancel }) => {
  // Create a clean Date object for today
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day to avoid time zone issues

  const [formData, setFormData] = useState({
    runName: '',
    startDate: today,
    endDate: today,
    subject: '',
    fromEmail: 'pella@e.pella.com',
    toEmail: '',
    emailType: 'PROMOTION',
    offerMatrix: '',
  });

  const [errors, setErrors] = useState({});

  const getOfferMatrixLabel = () => {
    if (formData.emailType === 'PROMOTION') {
      return 'Promotion Offer Matrix (Google Sheet URL) *';
    } else if (formData.emailType === 'REMINDER') {
      return 'Reminder Offer Matrix (Google Sheet URL) *';
    }
    return 'Offer Matrix (Google Sheet URL) *';
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }
    if (!formData.emailType) {
      newErrors.emailType = 'Email type is required';
    }
    if (!formData.offerMatrix) {
      newErrors.offerMatrix = 'Offer Matrix URL is required';
    }

    // Date range validation
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (startDate > endDate) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    // Email validation
    if (formData.toEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.toEmail)) {
        newErrors.toEmail = 'Please enter a valid email address';
      }
    }

    // URL validation for Google Sheet
    if (formData.offerMatrix) {
      const urlRegex = /^https:\/\/docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9-_]+/;
      if (!urlRegex.test(formData.offerMatrix)) {
        newErrors.offerMatrix = 'Please enter a valid Google Sheets URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Box style={style.formContainer}>
      <Card style={style.card}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Run Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Run Name (optional)"
                  variant="outlined"
                  value={formData.runName}
                  onChange={(e) => handleInputChange('runName', e.target.value)}
                  placeholder="Enter run name"
                  style={style.formField}
                />
              </Grid>

              {/* From Email */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="From Email *"
                  variant="outlined"
                  value={formData.fromEmail}
                  disabled
                  style={style.formField}
                />
              </Grid>

              {/* Date Range */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Date Range *
                </Typography>
                <Box style={style.dateRangeContainer}>
                  <DatePickerMui
                    label="Start Date"
                    defaultValue={formData.startDate}
                    handleDateChange={(value) => handleInputChange('startDate', value)}
                    style={{ width: '200px' }}
                    passUnformattedDate={true}
                    useDDMMYYFormat={true}
                  />
                  <Typography variant="body2">to</Typography>
                  <DatePickerMui
                    label="End Date"
                    defaultValue={formData.endDate}
                    handleDateChange={(value) => handleInputChange('endDate', value)}
                    style={{ width: '200px' }}
                    passUnformattedDate={true}
                    useDDMMYYFormat={true}
                    minDate={formData.startDate || null}
                  />
                </Box>
                {(errors.startDate || errors.endDate) && (
                  <Typography color="error" variant="caption">
                    {errors.startDate || errors.endDate}
                  </Typography>
                )}
              </Grid>

              {/* Subject */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Subject (optional)"
                  variant="outlined"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  placeholder="Enter email subject"
                  style={style.formField}
                />
              </Grid>

              {/* To Email */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="To Email (optional)"
                  variant="outlined"
                  value={formData.toEmail}
                  onChange={(e) => handleInputChange('toEmail', e.target.value)}
                  placeholder="Enter recipient email"
                  style={style.formField}
                  error={!!errors.toEmail}
                  helperText={errors.toEmail}
                />
              </Grid>

              {/* Email Type */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email Type *"
                  variant="outlined"
                  select
                  value={formData.emailType}
                  onChange={(e) => handleInputChange('emailType', e.target.value)}
                  style={style.formField}
                  error={!!errors.emailType}
                  helperText={errors.emailType}
                >
                  {emailTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Promotion Offer Matrix */}
              <Grid item xs={12}>
                <TextField
                  label={getOfferMatrixLabel()}
                  variant="outlined"
                  value={formData.offerMatrix}
                  onChange={(e) => handleInputChange('offerMatrix', e.target.value)}
                  placeholder="Enter Google Sheet URL for offer matrix"
                  style={style.formField}
                  error={!!errors.offerMatrix}
                  helperText={errors.offerMatrix}
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box style={style.buttonContainer}>
                  <Button type="submit" variant="contained" sx={style.startButton}>
                    Start Validation
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    sx={style.cancelButton}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

NewRunForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default NewRunForm;
